import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "./SearchSelect.css";

const PANEL_MAX_HEIGHT = 340;

// Searchable single-select dropdown (ported from the reference admin's app-search-select).
// Works standalone or inside antd <Form.Item> (which injects value / onChange / id).
// options: [{ value, label, hint?, disabled? }]
const SearchSelect = ({
  options = [],
  value,
  onChange,
  id,
  icon,
  placeholder = "Select",
  searchPlaceholder = "Search",
  emptyText = "No results found",
  loading = false,
  disabled = false,
  allowClear = false,
  showSearch, // default: only when there are more than 6 options
  alignRight = false,
  block = false, // full width of the parent
  size = "md", // "md" (38px) | "sm" (32px)
  width,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [pos, setPos] = useState(null);
  const btnRef = useRef(null);
  const panelRef = useRef(null);
  const searchRef = useRef(null);

  const hasSearch = showSearch ?? options.length > 6;
  const selected = options.find((o) => o.value === value);
  const hasValue = value !== undefined && value !== null && value !== "";

  const visibleOptions = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) =>
      String(o.label ?? "").toLowerCase().includes(q),
    );
  }, [options, search]);

  // The panel is portalled to <body> with fixed positioning so scrolling
  // containers (e.g. dialog bodies) never clip it.
  const place = useCallback(() => {
    const rect = btnRef.current?.getBoundingClientRect();
    if (!rect) return;
    const below = window.innerHeight - rect.bottom;
    const openUp = below < PANEL_MAX_HEIGHT && rect.top > below;
    setPos({
      left: alignRight ? undefined : rect.left,
      right: alignRight ? window.innerWidth - rect.right : undefined,
      top: openUp ? undefined : rect.bottom + 6,
      bottom: openUp ? window.innerHeight - rect.top + 6 : undefined,
      minWidth: rect.width,
    });
  }, [alignRight]);

  useEffect(() => {
    if (!open) return;
    setSearch("");
    place();
    searchRef.current?.focus();

    const onKey = (e) => e.key === "Escape" && setOpen(false);
    // Close when anything outside the panel scrolls; reposition on resize.
    const onScroll = (e) => {
      if (panelRef.current && panelRef.current.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", place);
    return () => {
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", place);
    };
  }, [open, place]);

  const select = (option) => {
    if (option.disabled) return;
    setOpen(false);
    if (option.value !== value) onChange?.(option.value);
  };

  const clear = (e) => {
    e.stopPropagation();
    onChange?.(undefined);
  };

  const wrapClass = ["ss-wrap", block && "ss-block", size === "sm" && "ss-sm"]
    .filter(Boolean)
    .join(" ");

  return (
    <div
      className={wrapClass}
      style={width && !block ? { width } : undefined}
    >
      <button
        ref={btnRef}
        id={id}
        type="button"
        className={`ss-btn${open ? " open" : ""}`}
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        {icon && (
          <span className="material-symbols-outlined ss-btn-icon">{icon}</span>
        )}
        <span className={`ss-btn-text${selected ? "" : " placeholder"}`}>
          {loading ? "Loading..." : selected?.label ?? placeholder}
        </span>
        {allowClear && hasValue && !disabled && (
          <span
            className="material-symbols-outlined ss-clear"
            role="button"
            aria-label="Clear"
            onClick={clear}
          >
            close
          </span>
        )}
        <span className="material-symbols-outlined ss-caret">
          {open ? "arrow_drop_up" : "arrow_drop_down"}
        </span>
      </button>

      {open &&
        pos &&
        createPortal(
          <>
            {/* Click anywhere else closes the panel */}
            <div className="ss-backdrop" onClick={() => setOpen(false)} />

            <div
              ref={panelRef}
              className="ss-panel"
              style={{
                left: pos.left,
                right: pos.right,
                top: pos.top,
                bottom: pos.bottom,
                minWidth: pos.minWidth,
              }}
            >
              {hasSearch && (
                <div className="ss-search">
                  <span className="material-symbols-outlined ss-search-icon">
                    search
                  </span>
                  <input
                    ref={searchRef}
                    type="search"
                    className="ss-input"
                    value={search}
                    maxLength={100}
                    placeholder={searchPlaceholder}
                    aria-label={searchPlaceholder}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && visibleOptions.length === 1) {
                        select(visibleOptions[0]);
                      }
                    }}
                  />
                </div>
              )}

              <div
                className={`ss-list${hasSearch ? "" : " no-search"}`}
                role="listbox"
              >
                {visibleOptions.map((option) => (
                  <button
                    type="button"
                    key={option.value}
                    role="option"
                    aria-selected={option.value === value}
                    disabled={option.disabled}
                    className={`ss-item${
                      option.value === value ? " is-selected" : ""
                    }`}
                    onClick={() => select(option)}
                  >
                    <span className="ss-item-name">{option.label}</span>
                    {option.hint && (
                      <span className="ss-item-hint">{option.hint}</span>
                    )}
                    {option.value === value && (
                      <span className="material-symbols-outlined ss-check">
                        check
                      </span>
                    )}
                  </button>
                ))}
                {visibleOptions.length === 0 && (
                  <p className="ss-empty">{emptyText}</p>
                )}
              </div>
            </div>
          </>,
          document.body,
        )}
    </div>
  );
};

export default SearchSelect;
